import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_ColorsAdvanced = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">🎨 Advanced CSS: Colors</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        CSS offers many ways to define colors — from simple names to advanced
        color models like <b>RGB, HSL, HWB, and LCH</b>. Mastering them gives
        you precise control over web design.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #e9d5ff, #c4b5fd)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#5b21b6" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            CSS supports <b>140+ named colors</b> like <code>red</code>,
            <code>teal</code>, <code>lightcoral</code>.
          </li>
          <li>
            <code>HEX</code>, <code>RGB</code>, and <code>HSL</code> are the
            most widely used formats.
          </li>
          <li>
            Modern CSS also supports <code>HWB</code> (Hue, Whiteness,
            Blackness) and <code>LCH</code> (Lightness, Chroma, Hue).
          </li>
          <li>
            Add transparency with <code>rgba()</code>, <code>hsla()</code>, or{" "}
            <code>#RRGGBBAA</code>.
          </li>
          <li>
            Gradients (linear, radial, conic) are treated as{" "}
            <code>background-image</code>, not plain colors.
          </li>
        </ul>
      </div>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Named color */
color: tomato;

/* HEX */
color: #ff5733;
color: #ff573380; /* with alpha (50% transparency) */

/* RGB & RGBA */
color: rgb(255, 87, 51);
color: rgba(255, 87, 51, 0.5);

/* HSL & HSLA */
color: hsl(12, 100%, 60%);
color: hsla(12, 100%, 60%, 0.5);

/* HWB */
color: hwb(200 20% 40%);

/* LCH (CSS Color Level 4) */
color: lch(70% 50 320);`}
        />
      </div>

      {/* Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">⚡ Examples</h2>

        {/* Named Colors */}
        <h3 className="section-subtitle">1. Named Colors</h3>
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {["tomato", "teal", "gold", "lightcoral", "indigo"].map((c) => (
            <div
              key={c}
              style={{
                background: c,
                width: "90px",
                height: "60px",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                fontWeight: "bold",
                textShadow: "1px 1px 3px rgba(0,0,0,0.6)",
              }}
            >
              {c}
            </div>
          ))}
        </div>

        {/* HEX */}
        <h3 className="section-subtitle">2. HEX & Alpha HEX</h3>
        <CodeCopyBlock
          code={`color: #3498db; /* blue */ \ncolor: #3498db80; /* 50% transparent */`}
        />
        <div style={{ display: "flex", gap: "10px" }}>
          <div
            style={{
              background: "#3498db",
              width: "120px",
              height: "60px",
              borderRadius: "8px",
            }}
          />
          <div
            style={{
              background: "#3498db80",
              width: "120px",
              height: "60px",
              borderRadius: "8px",
              border: "2px dashed #000",
            }}
          />
        </div>

        {/* RGB / RGBA */}
        <h3 className="section-subtitle">3. RGB / RGBA</h3>
        <CodeCopyBlock
          code={`color: rgb(255, 99, 71); /* tomato */\ncolor: rgba(255, 99, 71, 0.5); /* 50% transparent */`}
        />
        <div style={{ display: "flex", gap: "10px" }}>
          <div
            style={{
              background: "rgb(255, 99, 71)",
              width: "120px",
              height: "60px",
              borderRadius: "8px",
            }}
          />
          <div
            style={{
              background: "rgba(255, 99, 71, 0.5)",
              width: "120px",
              height: "60px",
              borderRadius: "8px",
              border: "2px dashed #000",
            }}
          />
        </div>

        {/* HSL / HSLA */}
        <h3 className="section-subtitle">4. HSL / HSLA</h3>
        <CodeCopyBlock
          code={`color: hsl(200, 80%, 50%);\ncolor: hsla(200, 80%, 50%, 0.5);`}
        />
        <div style={{ display: "flex", gap: "10px" }}>
          <div
            style={{
              background: "hsl(200, 80%, 50%)",
              width: "120px",
              height: "60px",
              borderRadius: "8px",
            }}
          />
          <div
            style={{
              background: "hsla(200, 80%, 50%, 0.5)",
              width: "120px",
              height: "60px",
              borderRadius: "8px",
              border: "2px dashed #000",
            }}
          />
        </div>

        {/* HWB */}
        <h3 className="section-subtitle">5. HWB (CSS4)</h3>
        <CodeCopyBlock code={`color: hwb(200 30% 20%);`} />
        <div
          style={{
            background: "hwb(200 30% 20%)",
            width: "120px",
            height: "60px",
            borderRadius: "8px",
          }}
        />

        {/* LCH */}
        <h3 className="section-subtitle">6. LCH (CSS4)</h3>
        <CodeCopyBlock code={`color: lch(70% 80 40);`} />
        <div
          style={{
            background: "lch(70% 80 40)",
            width: "120px",
            height: "60px",
            borderRadius: "8px",
          }}
        />

        {/* Gradient */}
        <h3 className="section-subtitle">7. Gradient Colors</h3>
        <CodeCopyBlock
          code={`background: linear-gradient(90deg, #ff6a00, #ee0979);\nbackground: radial-gradient(circle, #4facfe, #00f2fe);`}
        />
        <div
          style={{
            background: "linear-gradient(90deg, #ff6a00, #ee0979)",
            width: "200px",
            height: "60px",
            borderRadius: "8px",
            marginBottom: "0.5rem",
          }}
        />
        <div
          style={{
            background: "radial-gradient(circle, #4facfe, #00f2fe)",
            width: "200px",
            height: "60px",
            borderRadius: "8px",
          }}
        />
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Explore advanced color mixing using <code>color-mix()</code> and
          variable-driven themes with <code>CSS Custom Properties</code> to make
          your designs even more dynamic.
        </p>
      </div>
    </div>
  );
};

export default CSS_ColorsAdvanced;
